/**
 * Canonical helper: is an activity "past" and therefore immutable?
 *
 * Rules:
 *  - timing_mode = 'date_time': past when cutoff (end_date or start_date) <= now
 *  - timing_mode = 'season': past when end_date is set AND end_date < now
 *  - timing_mode = 'open': past when end_date is set AND end_date < now
 *  - timing_mode = 'anytime': never past (legacy alias)
 *  - courses: past when no future occurrences remain (must be checked via
 *    fetchCourseOccurrenceInfo separately — this helper handles the non-course case
 *    and accepts an optional futureOccurrences override for courses)
 *
 * This is used for mutation guards (read-only, no delete, no edit).
 */

import { getEventTimeBucket } from './timeBucket';

export interface IsPastActivityInput {
  event_type?: string;
  timing_mode?: string;
  start_date?: string | null;
  end_date?: string | null;
  /** For courses: number of future occurrences. If undefined, falls back to date logic. */
  futureOccurrencesCount?: number;
}

export function isPastActivity(
  event: IsPastActivityInput,
  now: Date = new Date(),
): boolean {
  const timingMode = event.timing_mode || 'date_time';

  // Legacy anytime activities are never "past"
  if (timingMode === 'anytime') return false;

  // Open activities: only past if explicit end_date has passed.
  // If end_date is null, the activity stays open indefinitely and is never past.
  // This handles:
  //   - open without dates (both null) → never past ✓
  //   - open from start only (end null) → never past ✓
  //   - open start–end (both set) → past only if end has passed ✓
  if (timingMode === 'open') {
    if (!event.end_date) return false; // No explicit end → not past
    return new Date(event.end_date) < now;
  }

  // Courses with known occurrence data: past only if zero future occurrences
  if (event.event_type === 'course' && event.futureOccurrencesCount !== undefined) {
    return event.futureOccurrencesCount === 0;
  }

  // Season: only past if end_date is set and has passed
  if (timingMode === 'season') {
    if (!event.end_date) return false;
    return new Date(event.end_date) < now;
  }

  // date_time: use timeBucket
  const { bucket } = getEventTimeBucket(
    { start_date: event.start_date, end_date: event.end_date },
    now,
  );
  return bucket === 'past';
}
